import { Modal } from "@mui/material";
import { createBrowserHistory } from "history";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { activeServiceId, pageIndicator } from "../apollo";
import { useReactiveVar } from "@apollo/client";
import exit from "../assets/icon/exit.png";
import {
  ComponentTabLink,
  ITabLink,
  ITabTitle,
  PageType,
} from "../components/component-tab";
import { PaginationComponent } from "../components/pagination";
import { TableError } from "../components/table-error";
import { useEditOrderStatus } from "../hooks/useEditOrderStatus";
import { useListOrder } from "../hooks/useListOrder";
import { AsideAdminComponent } from "../layout/aside-admin";
import { ListOrder } from "./admin-list-order";
import {
  OrderStatus,
  ListOrderInput,
  EditOrderStatusInput,
  EditOrderStatusMutation,
} from "../generated/graphql";

export const AdminListOrderStart = () => {
  const word = "배송중";
  const tabList: ITabLink[] = [
    { text: "주문확인", to: "/order" },
    { text: "출고대기", to: "/release" },
    { text: "배송준비중", to: "/order/delivery" },
    { text: "배송중", to: "/order/start", selected: true },
    { text: "배송완료", to: "/order/end" },
    { text: "교환/환불", to: "/order/exchange" },
    { text: "배송확정", to: "/order/confirm" },
  ];
  const location = useLocation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [change, setChange] = React.useState<OrderStatus>(OrderStatus.HandOut);
  const [page, setPage] = useState<number>(1);
  const [search, setSearch] = useState<string>("");
  const currentServiceId = useReactiveVar(activeServiceId);
  const [stateId, setStateId] = useState<number>(1);
  const [checkedLists, setCheckedLists] = useState<ListOrder[]>([]);
  const [allChecked, setAllChecked] = useState<boolean>(false);
  const stitle: ITabTitle = { text: "배송관리" };

  const StateHandler = (id: number) => {
    setStateId(id);
    setOpen(true);
  };
  const [listOrder, { data, loading, refetch }] = useListOrder();
  const listOrderHandler = (
    page: number | null,
    search: string | null,
    statuses: OrderStatus[],
    start?: Date | null,
    end?: Date | null
  ) => {
    const input: ListOrderInput = {
      ...(page && { page: +page }),
      ...(search && { search: decodeURI(search) }),
      ...(statuses.length > 0 && { statuses }),
      ...(start && { start }),
      ...(end && { end }),
      ...(activeServiceId() && { serviceId: activeServiceId()! }),
    };
    listOrder({ variables: { input } });
  };

  // --- 상태 변경 ---
  const onEditCompleted = async (data: EditOrderStatusMutation) => {
    const {
      editOrderStatus: { ok, err },
    } = data;
    if (ok) {
      // alert(`주문상태가 수정되었습니다.`);
      await refetch();
    } else {
      alert(err);
    }
  };
  const [
    editOrderStatus,
    { loading: editOrderStatusLoading },
  ] = useEditOrderStatus(onEditCompleted);
  const editOrderStatusHandler = (orderId: number, status: OrderStatus) => {
    if (!editOrderStatusLoading) {
      const editOrderStatusInput: EditOrderStatusInput = {
        orderId,
        status,
      };
      editOrderStatus({ variables: { input: editOrderStatusInput } });
    }
  };

  // --- CheckBox ---
  const setAllCheckedHandler = () => {
    setAllChecked(!allChecked);
    if (allChecked) setCheckedLists([]);

    if (!allChecked && data?.listOrder.orders) {
      setCheckedLists([]);
      for (const start of data?.listOrder.orders) {
        setCheckedLists((checkedList) => [...checkedList, start]);
      }
    }
  };

  const setCheckedListHandler = (startId: number, data: any) => {
    if (Boolean(checkedLists.find((checkedId) => checkedId.id === startId))) {
      setCheckedLists(
        checkedLists.filter((checkedId) => checkedId.id !== startId)
      );
    } else {
      setCheckedLists((originalList) => [...originalList, data]);
    }
  };
  useEffect(() => {
    if (checkedLists.length === data?.listOrder.orders?.length)
      setAllChecked(true);
    else setAllChecked(false);
  }, [checkedLists]);

  // --- Search ---
  const setSearchHandler = (search: string) => {
    navigate({
      pathname: "/order/start",
      search: `${search ? `?search=${search}` : ""}`,
    });
    const statuses: OrderStatus[] = [
      OrderStatus.HandOut,
      OrderStatus.Drone,
      OrderStatus.Port,
      OrderStatus.Stored,
      OrderStatus.PendingDelivered,
    ];
    listOrderHandler(1, search, statuses);
  };

  // --- Page ---
  const pageHandler = (page: number) => {
    const search =
      location.search.split("search=").length > 1
        ? location.search.split("search=")[1].split("&")[0]
        : null;
    setPage(page);
    navigate({
      pathname: "/order/start",
      search: `?page=${page}${search ? `&search=${search}` : ""}`,
    });
    const statuses: OrderStatus[] = [
      OrderStatus.HandOut,
      OrderStatus.Drone,
      OrderStatus.Port,
      OrderStatus.Stored,
      OrderStatus.PendingDelivered,
    ];
    listOrderHandler(page, search, statuses);
  };
  // --- Period ---
  const periodHandler = (sDate?: Date, eDate?: Date) => {
    const statuses: OrderStatus[] = [
      OrderStatus.HandOut,
      OrderStatus.Drone,
      OrderStatus.Port,
      OrderStatus.Stored,
      OrderStatus.PendingDelivered,
    ];
    listOrderHandler(1, search, statuses, sDate, eDate);
  };

  useEffect(() => {
    pageIndicator("delivery");
    setPage(
      location.search.split("page=").length > 1
        ? +location.search.split("page=")[1].split("&")[0]
        : 1
    );
    setSearch(
      location.search.split("search=").length > 1
        ? location.search.split("search=")[1].split("&")[0]
        : ""
    );
    const statuses: OrderStatus[] = [
      OrderStatus.HandOut,
      OrderStatus.Drone,
      OrderStatus.Port,
      OrderStatus.Stored,
      OrderStatus.PendingDelivered,
    ];
    listOrderHandler(page, search, statuses);
  }, [search, page, currentServiceId]);

  return (
    <div className="bg-main bg-opacity-50 w-full h-full min-h-screen">
      <div className="w-full mx-auto h-full min-h-screen  bg-gray-100">
        <main id="main">
          <div className="grid grid-cols-[200px_1fr] max-w-full">
            <AsideAdminComponent />
            <div className="mypages bg-gray-100 w-full min-w-[1200px] overflow-hidden">
              <div className="orderWrap">
                <div className="h-fix-content relative">
                  <div className={`w-full h-full px-10 py-8`}>
                    <ComponentTabLink
                      tabs={tabList}
                      stitle={stitle}
                      type={PageType.Start}
                      dataList={checkedLists}
                      setSearchs={setSearchHandler}
                      setDateSearch={periodHandler}
                      refetch={refetch}
                    />
                    {/* 목록 */}
                    <div className=" shadow-lg h-[800px] h-fix-content w-full overflow-auto bg-white">
                      <table className="align-center w-[1796px]  text-sm  border-[#F9F8F9] ">
                        <colgroup>
                          <col width="36"></col>
                          <col width="40"></col>
                          <col width="64"></col>
                          <col width="88"></col>
                          <col width="112"></col>
                          <col width="88"></col>
                          <col width="88"></col>
                          <col width="168"></col>
                          <col width="168"></col>
                          <col width="48"></col>
                          <col width="88"></col>
                          <col width="88"></col>
                          <col width="168"></col>
                          <col width="168"></col>
                          <col width="128"></col>
                          <col width="88"></col>
                          <col width="80"></col>
                        </colgroup>
                        <thead>
                          <tr className=" border-b border-[#F9F8F9] h-8">
                            <th className="text-[#8B8C8C] leading-3 min-w-[36px]">
                              <input
                                type="checkbox"
                                name="allChk"
                                checked={allChecked}
                                onChange={setAllCheckedHandler}
                                className="w-5 h-5"
                              />
                            </th>
                            <th className="text-[#8B8C8C] min-w-[40px]">no</th>
                            <th className="text-[#8B8C8C] min-w-[64px]">
                              상태
                            </th>
                            <th className="text-[#8B8C8C] min-w-[88px]">
                              주문번호
                            </th>
                            <th className="text-[#8B8C8C] min-w-[112px]">
                              QR코드/송장번호
                            </th>
                            <th className="text-[#8B8C8C] min-w-[88px]">
                              주문날짜
                            </th>
                            <th className="text-[#8B8C8C] min-w-[88px]">
                              담당자
                            </th>
                            <th className="text-[#8B8C8C] min-w-[168px]">
                              상품명
                            </th>
                            <th className="text-[#8B8C8C] min-w-[168px]">
                              상품옵션
                            </th>
                            <th className="text-[#8B8C8C] min-w-[48px]">
                              수량
                            </th>
                            <th className="text-[#8B8C8C] min-w-[88px]">
                              구매자
                            </th>
                            <th className="text-[#8B8C8C] min-w-[88px]">
                              수취인
                            </th>
                            <th className="text-[#8B8C8C] min-w-[168px]">
                              주소
                            </th>
                            <th className="text-[#8B8C8C] min-w-[168px]">
                              배송메시지
                            </th>
                            <th className="text-[#8B8C8C] min-w-[128px]">
                              연락처
                            </th>

                            <th className="text-[#8B8C8C] min-w-[88px]">
                              무게
                            </th>
                            <th className="text-[#8B8C8C] min-w-[80px]">
                              재배송
                            </th>
                          </tr>
                        </thead>
                        <tbody className="">
                          {!loading &&
                            data?.listOrder.orders &&
                            data.listOrder.orders.map((start, index) => (
                              <tr
                                key={`start-list-${start.id}`}
                                className="border-b border-[#F9F8F9] h-[68px]"
                              >
                                <td className="table-text">
                                  <input
                                    type="checkbox"
                                    className="h-5 w-5"
                                    checked={
                                      checkedLists.find(
                                        (checkedId) => checkedId.id === start.id
                                      ) !== undefined
                                    }
                                    onChange={(e) => {
                                      setCheckedListHandler(start.id, start);
                                    }}
                                  />
                                </td>
                                <td className="table-text">
                                  {" "}
                                  {(page - 1) * 20 + index + 1}
                                </td>
                                <td className="table-text">
                                  <button
                                    className="bg-[#555B94] rounded-lg py-[4px] px-[3px]  w-[48px] h-10 text-white keep-all"
                                    onClick={() => {
                                      StateHandler(start.id);
                                      setChange(start.status);
                                    }}
                                  >
                                    {start.status === "HandOut"
                                      ? "배송중"
                                      : start.status === "Drone"
                                        ? "드론 비행"
                                        : start.status === "Port"
                                          ? "최종 배송준비"
                                          : start.status === "Stored"
                                            ? "픽업포트"
                                            : "최종 배송"}
                                  </button>
                                </td>
                                <td className="table-text break-all">
                                  {start.receipt?.receiptNum}
                                </td>
                                <td className="table-text font-bold"></td>
                                <td className="table-text font-bold">
                                  {start.createdAt &&
                                    moment(start.createdAt).format("YYMMDD")}
                                  <br />
                                  {start.createdAt &&
                                    moment(start.createdAt).format("h : mm")}
                                </td>
                                <td className="table-text">
                                  {start.manager?.name}
                                </td>
                                <td className="table-text">
                                  {start.good?.title}
                                </td>
                                <td className="table-text">
                                  {start.goodOptionName}
                                </td>
                                <td className="table-text">{start.quantity}</td>
                                <td className="table-text">
                                  {start.receipt?.user?.name}
                                </td>
                                <td className="table-text">
                                  {start.receipt?.receiver
                                    ? start.receipt.receiver
                                    : start.receipt?.user?.name}
                                </td>
                                <td className="table-text">
                                  {start.receipt?.address}
                                </td>
                                <td className="table-text">
                                  {start.receipt?.message}
                                </td>
                                <td className="table-text">
                                  {start.receipt?.phone
                                    ? start.receipt.phone
                                    : start.receipt?.user?.phone}
                                </td>
                                <td className="table-text">
                                  {start.good?.weight &&
                                    start.good?.weight + "g"}
                                </td>
                                <td className="table-text">
                                  {start.redelivery}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                      {loading && (
                        <TableError errorMessage={`정보를 불러오는중입니다`} />
                      )}
                      {!loading &&
                        data?.listOrder.orders &&
                        data.listOrder.orders.length < 1 && (
                          <TableError
                            errorMessage={`${word} 정보가 없습니다`}
                          />
                        )}

                      {!loading &&
                        data?.listOrder.orders &&
                        data.listOrder.orders.length > 0 && (
                          <PaginationComponent
                            page={page}
                            totalPages={Number(data.listOrder.totalPages)}
                            totalResults={Number(data.listOrder.totalResults)}
                            pageHandler={pageHandler}
                          />
                        )}
                      <Modal
                        open={open}
                        onClose={() => {
                          setOpen(false);
                        }}
                      >
                        <div className="max-w-[360px] w-full absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2   bg-[#F9F8F9] rounded-lg">
                          <div className="text-center py-2 h-10 text-white font-bold bg-[#3D3E49] rounded-t-lg  ">
                            <div className="flex flex-wrap justify-between">
                              <p className="w-[240px] ml-[40px] font-bold text-base">
                                배송 상태변경
                              </p>
                              <img
                                src={exit}
                                alt="닫기"
                                className="w-6 mr-2 cursor-pointer"
                                onClick={() => setOpen(false)}
                              ></img>
                            </div>
                          </div>
                          <div className="flex flex-wrap content-center">
                            <div className="px-10 py-10">
                              <div className="grid grid-rows-6 gap-y-4">
                                <p className="font-bold">배송준비</p>
                                <div>
                                  <input
                                    type="radio"
                                    name="order"
                                    id="pending"
                                    checked={change === OrderStatus.Pending}
                                    onChange={(e) => {
                                      setChange(OrderStatus.Pending);
                                    }}
                                  ></input>
                                  <span className="ml-4">배송 준비</span>
                                </div>
                                <p className="font-bold">배송중</p>

                                <div>
                                  <input
                                    type="radio"
                                    name="order"
                                    id="Drone"
                                    value="Drone"
                                    checked={change === OrderStatus.Drone}
                                    onChange={(e) => {
                                      setChange(OrderStatus.Drone);
                                    }}
                                  ></input>
                                  <span className="ml-4">드론 비행</span>
                                </div>
                                <div>
                                  <input
                                    type="radio"
                                    name="order"
                                    id="Port"
                                    value="Port"
                                    checked={change === OrderStatus.Port}
                                    onChange={(e) => {
                                      setChange(OrderStatus.Port);
                                    }}
                                  ></input>
                                  <span className="ml-4">최종 배송준비</span>
                                </div>
                                <div>
                                  <input
                                    type="radio"
                                    name="order"
                                    id="Port"
                                    value="Stored"
                                    checked={change === OrderStatus.Stored}
                                    onChange={(e) => {
                                      setChange(OrderStatus.Stored);
                                    }}
                                  ></input>
                                  <span className="ml-4">픽업포트</span>
                                </div>
                                <div>
                                  <input
                                    type="radio"
                                    name="order"
                                    id="PendingDelivered"
                                    value="PendingDeliverd"
                                    checked={
                                      change === OrderStatus.PendingDelivered
                                    }
                                    onChange={(e) => {
                                      setChange(OrderStatus.PendingDelivered);
                                    }}
                                  ></input>
                                  <span className="ml-4">최종 배송</span>
                                </div>
                                <p className="font-bold">배송 완료</p>
                                <div>
                                  <input
                                    type="radio"
                                    name="order"
                                    id="check"
                                    value="Delivered"
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setChange(OrderStatus.Delivered);
                                      }
                                    }}
                                  ></input>
                                  <span className="ml-4">배송 완료</span>
                                </div>
                                <p className="font-bold">환불/교환</p>
                                <div>
                                  <input
                                    type="radio"
                                    name="order"
                                    id="Refunded"
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setChange(OrderStatus.Refunded);
                                      }
                                    }}
                                  ></input>
                                  <span className="ml-4">환불</span>
                                </div>
                                <div>
                                  <input
                                    type="radio"
                                    name="order"
                                    id="end"
                                    value="Exchange"
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setChange(OrderStatus.Exchange);
                                      }
                                    }}
                                  ></input>
                                  <span className="ml-4">교환</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <button
                            className="w-full h-14 bg-[#F0F0F4] rounded-b-lg"
                            onClick={() => {
                              editOrderStatusHandler(stateId, change);
                              setOpen(false);
                            }}
                          >
                            확인
                          </button>
                        </div>
                      </Modal>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};
